<template>
	<ja-modal :show="show" class="intro-modal">
		<content>
			<div class="avatar-icon">
				<img :src="avatarImg" alt="" />
			</div>
			<div class="title-wrapper">
				<h1 :aria-label="$t('confirmBudgetTitle')">
					{{ $t('confirmBudgetTitle') }}
				</h1>
				<h2 class="body-text-1">{{ $t('confirmBudgetDescription') }}</h2>
			</div>
			<div v-if="categoryTabs" class="budget-creator-wrapper">
				<h3
					class="budget-counter body-text-1"
					:class="{ error: noBudgetLeft }"
					:aria-label="$t('householdNMIAbbr', { amount: maxBudgetRemaining })"
				>
					{{ $t('householdNMI') }}:
					<strong>{{ maxBudgetRemaining | currency }}</strong>
				</h3>
				<div class="budget-creator">
					<div
						class="category-cnt savings"
						:class="{ expanded: savings.expanded }"
					>
						<button
							class="category-header"
							tabindex="0"
							:aria-label="
								$t('budgetSection', {
									category: savings.name,
									amount: savings.amount
								})
							"
							role="button"
							:aria-expanded="(!!savings.expanded).toString()"
							@mousedown.prevent
							@click="toggleSliders($event.target, savings)"
						>
							<div class="heading-3" aria-hidden="true">
								{{ savings.name }}
								<span
									v-show="!hasValidOptions(savings)"
									class="text--small text--danger"
								>
									{{ $t('invalidBudgetOption') }}
								</span>
							</div>
							<div class="heading-4" aria-hidden="true">
								{{ savings.amount | currency }}
							</div>
							<svgicon name="arrow-right"></svgicon>
						</button>
						<transition name="slide" mode="out-in">
							<div
								v-if="savings.expanded !== null"
								v-show="savings.expanded"
								class="sliders-cnt"
							>
								<div class="legend-wrapper">
									<ja-budget-slider-legend></ja-budget-slider-legend>
								</div>
								<div
									v-for="(subcategory, i) in savingsSubcategories"
									:key="i"
									class="slider-wrapper"
								>
									<div class="slider-info">
										<div class="slider-info-name">
											<label class="heading-4 slider-name">{{
												$t(subcategory.name)
											}}</label>
											<div class="slider-info-title">
												<div
													v-if="subcategory.description"
													class="tooltip-wrapper"
												>
													<button
														class="tooltip-button"
														:aria-label="$t('moreInfoSavings')"
														@click="savingsGoalTooltip(i)"
														@mousedown.prevent
													>
														<svgicon name="savings-info" :original="true">
														</svgicon>
													</button>
													<ja-tooltip
														v-show="
															i === selectedSubcategoryIndex && showTooltipDesc
														"
														role="status"
														class="left"
														width-msg="435px"
														>{{ $t(subcategory.description) }}</ja-tooltip
													>
												</div>
											</div>
										</div>
										<div
											v-if="subcategory.hasOwnProperty('totalAmount')"
											class="saving-amount-wrapper"
										>
											<ja-currency-input
												v-model="subcategory.totalAmount"
												:aria-label="
													$t('totalGoalAmount', { category: subcategory.name })
												"
												:placeholder="$t('totalAmount')"
												:class="validationClasses(subcategory.totalAmount)"
												@input="announceTotalSavings(subcategory)"
											>
											</ja-currency-input>
										</div>

										<div class="body-text-2">{{ $t('monthlyAmount') }}</div>
										<div
											v-if="!subcategory.hasOwnProperty('totalAmount')"
											class="tooltip-wrapper"
										>
											<button
												class="tooltip-button"
												:aria-label="$t('moreInfoSavings')"
												@click="toggleTooltip"
												@mousedown.prevent
											>
												<svgicon
													v-if="!subcategory.hasOwnProperty('totalAmount')"
													name="savings-info"
													:original="true"
												></svgicon>
											</button>
											<ja-tooltip
												v-show="showTooltip"
												role="status"
												class="left"
												>{{ $t('savingsTooltip') }}</ja-tooltip
											>
										</div>
									</div>
									<div class="budget-slider-wrapper">
										<ja-budget-slider
											v-model="subcategory.amount"
											:budget="maxSavingsRemaining"
											:name="subcategory.name"
											:min="0"
											:max="getMaxSliderValue(subcategory.amount)"
											:recommended-min="subcategory.minimum"
											:recommended-max="subcategory.maximum"
											:is-limit-value="noBudgetLeft"
											:limit-value="
												noBudgetLeft
													? savingsSubcategoriesMax[i].amount
													: subcategory.amount
											"
											:step="1"
											class="budget-slider"
											@input="onSavingSliderChanged(subcategory)"
										>
										</ja-budget-slider>
										<ja-currency-input
											v-model="subcategory.amount"
											:aria-label="
												$t('amountSaveCategory', { category: subcategory.name })
											"
											class="budget-input"
											:class="validationClasses(subcategory.amount)"
											:max="getMaxSliderValue(subcategory.amount)"
											:aria-value-max="netMonthlyIncome"
											:is-disabled="true"
											@input="onSavingSliderChanged(subcategory)"
										>
										</ja-currency-input>
									</div>
									<div
										v-show="subcategory.amount < subcategory.minimum"
										class="msg min-msg"
									>
										{{ $t('sliderMinMsg') }}
									</div>
									<div
										v-show="subcategory.amount > subcategory.maximum"
										class="msg max-msg"
									>
										{{ $t('sliderMaxMsg') }}
									</div>
								</div>
							</div>
						</transition>
					</div>

					<div
						v-if="debts.subcategories.length"
						class="category-cnt debts"
						:class="{ expanded: debts.expanded }"
					>
						<button
							class="category-header"
							tabindex="0"
							:aria-label="
								$t('budgetSection', {
									category: debts.name,
									amount: debts.amount
								})
							"
							role="button"
							:aria-expanded="(!!debts.expanded).toString()"
							@click="toggleSliders($event.target, debts)"
							@mousedown.prevent
						>
							<div class="heading-3" aria-hidden="true">
								{{ debts.name }}
								<span
									v-show="!hasValidOptions(debts)"
									class="text--small text--danger"
								>
									{{ $t('invalidBudgetOption') }}
								</span>
							</div>
							<div class="heading-4" aria-hidden="true">
								{{ debts.amount | currency }}
							</div>
							<svgicon name="arrow-right"></svgicon>
						</button>
						<transition name="slide" mode="out-in">
							<div
								v-if="debts.expanded"
								v-show="debts.expanded"
								class="sliders-cnt"
							>
								<div class="legend-wrapper">
									<ja-budget-slider-legend></ja-budget-slider-legend>
								</div>
								<div
									v-for="(subcategory, i) in debts.subcategories"
									:key="i"
									class="slider-wrapper"
								>
									<div class="slider-info">
										<label class="heading-4 slider-name">{{
											$t(subcategory.name)
										}}</label>
										<div class="body-text-2">{{ $t('monthlyAmount') }}</div>
									</div>
									<div class="budget-slider-wrapper">
										<ja-budget-slider
											v-model="subcategory.budget"
											:budget="maxBudgetRemaining"
											:name="subcategory.name"
											:min="0"
											:max="getMaxSliderValue(subcategory.budget)"
											:recommended-min="subcategory.minimum"
											:recommended-max="subcategory.maximum"
											:step="1"
											class="budget-slider"
											@input="onDebtSliderChanged(subcategory)"
										>
										</ja-budget-slider>
										<ja-currency-input
											v-model="subcategory.budget"
											:aria-label="
												$t('amountSpentCategory', {
													category: subcategory.name
												})
											"
											class="budget-input"
											:class="validationClasses(subcategory.budget)"
											:max="getMaxSliderValue(subcategory.budget)"
											:aria-value-max="netMonthlyIncome"
											@input="onDebtSliderChanged(subcategory)"
										>
										</ja-currency-input>
									</div>
									<div
										v-show="subcategory.budget < subcategory.minimum"
										class="msg min-msg"
									>
										{{ $t('sliderMinMsg') }}
									</div>
									<div
										v-show="subcategory.budget > subcategory.maximum"
										class="msg max-msg"
									>
										{{ $t('sliderMaxMsg') }}
									</div>
								</div>
							</div>
						</transition>
					</div>

					<div
						v-for="(category, index) in categoryTabs"
						:key="index"
						class="category-cnt"
						:class="{ expanded: category.expanded }"
					>
						<button
							class="category-header"
							tabindex="0"
							:aria-label="
								$t('budgetSection', {
									category: category.name,
									amount: category.budget
								})
							"
							role="button"
							:aria-expanded="(!!category.expanded).toString()"
							@click="toggleSliders($event.target, category)"
							@mousedown.prevent
						>
							<div class="heading-3" aria-hidden="true">
								{{ category.name }}
								<span
									v-show="!hasValidOptions(category)"
									class="text--small text--danger"
								>
									{{ $t('invalidBudgetOption') }}
								</span>
							</div>
							<div class="heading-4" aria-hidden="true">
								{{ category.budget | currency }}
							</div>

							<svgicon name="arrow-right"></svgicon>
						</button>
						<transition name="slide" mode="out-in">
							<div
								v-if="category.expanded !== null"
								v-show="category.expanded"
								class="sliders-cnt"
							>
								<div class="legend-wrapper">
									<ja-budget-slider-legend></ja-budget-slider-legend>
								</div>
								<div
									v-for="(subcategory, i) in category.subcategories"
									:key="i"
									class="slider-wrapper"
								>
									<div class="slider-info">
										<label class="heading-4 slider-name">
											{{ $t(subcategory.name) }}
										</label>
									</div>
									<div class="budget-slider-wrapper">
										<ja-budget-slider
											v-model="subcategory.budget"
											:budget="maxBudgetRemaining"
											:name="subcategory.name"
											:min="0"
											:max="getMaxSliderValue(subcategory.budget)"
											:recommended-min="subcategory.minimum"
											:recommended-max="subcategory.maximum"
											:step="1"
											class="budget-slider"
											@input="onExpenseSliderChanged(category, subcategory)"
										>
										</ja-budget-slider>
										<ja-currency-input
											v-model="subcategory.budget"
											class="budget-input"
											:budget="maxSavingsRemaining"
											:class="validationClasses(subcategory.budget)"
											:max="getMaxSliderValue(subcategory.budget)"
											:aria-value-max="netMonthlyIncome"
											:aria-label="
												$t('amountSpentCategory', {
													category: subcategory.name
												})
											"
											@input="onExpenseSliderChanged(category, subcategory)"
										>
										</ja-currency-input>
									</div>
									<div
										v-show="subcategory.budget < subcategory.minimum"
										class="msg min-msg"
									>
										{{ $t('sliderMinMsg') }}
									</div>
									<div
										v-show="subcategory.budget > subcategory.maximum"
										class="msg max-msg"
									>
										{{ $t('sliderMaxMsg') }}
									</div>
								</div>
							</div>
						</transition>
					</div>
				</div>
				<div class="button-wrapper">
					<slot></slot>
					<ja-button
						ref="button"
						class="small"
						name="nextLifeScenario"
						:aria-disabled="isButtonDisabled"
						:disabled="requestSent"
						:aria-label="$t('saveYourBudgets')"
						@click.native="checkAndSaveBudget"
					>
						{{ $t('confirmBudgetBtn') }}
						<svgicon name="arrow-right"></svgicon>
					</ja-button>
				</div>
			</div>
		</content>
	</ja-modal>
</template>
<script>
import gql from 'graphql-tag';
import '../icons/arrow-right';
import '../icons/savings-info';
import UserData from '../../mixins/UserData';
import LifeStageData from '../../mixins/LifeStageData';
import ProfileData from '../../mixins/ProfileData';
import BudgetSlider from './elements/BudgetSlider';
import BudgetSliderLegend from './elements/BudgetSliderLegend';
import Button from '../ui/Button';
import CurrencyInput from '../ui/CurrencyInput';
import Tooltip from '../ui/Tooltip';
import ExpenseCategories from '../../mixins/ExpenseCategories';
import Savings from '../../mixins/Savings';
import Debts from '../../mixins/Debts';
import Accessibility from '../../mixins/Accessibility';
import StudentsService from '@/services/StudentsService';

import { debtNames, debtTypes } from '../../helpers/constants';
import Modal from '@/components/Modal/Modal';

export default {
	components: {
		jaBudgetSlider: BudgetSlider,
		jaButton: Button,
		jaCurrencyInput: CurrencyInput,
		jaTooltip: Tooltip,
		jaBudgetSliderLegend: BudgetSliderLegend,
		jaModal: Modal
	},
	mixins: [
		UserData,
		LifeStageData,
		ProfileData,
		ExpenseCategories,
		Savings,
		Debts,
		Accessibility
	],
	props: {
		show: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			savings: {
				expanded: null,
				name: this.$t('savings'),
				amount: 0,
				subcategories: []
			},
			debts: {
				expanded: null,
				name: this.$t('debts'),
				amount: 0,
				subcategories: []
			},
			showTooltip: false,
			requestSent: false,
			useValidations: false,
			showTooltipDesc: false,
			selectedSubcategoryIndex: -1,
			budgetCheckData: null,
			savingsSubcategoriesMax: []
		};
	},
	computed: {
		totalCategoriesBudget() {
			return this.categoryTabs.reduce((sum, item) => {
				if (item.budget >= 0) {
					return sum + parseInt(item.budget);
				}
			}, 0);
		},
		maxBudgetRemaining() {
			return (
				this.netMonthlyIncome -
				this.totalCategoriesBudget -
				this.savings.amount -
				this.debts.amount
			);
		},
		percentageOfNmiDividedByHundred() {
			return this.calculatePercentageOfNmiToSavings();
		},
		maxSavingsRemaining() {
			let maxAllowedSavings =
				this.netMonthlyIncome * this.percentageOfNmiDividedByHundred;

			if (this.savings.amount > 0 && maxAllowedSavings > 0) {
				maxAllowedSavings = maxAllowedSavings - this.savings.amount;
			}

			if (maxAllowedSavings < 0) {
				maxAllowedSavings = 0;
				return;
			}

			return maxAllowedSavings;
		},
		noBudgetLeft() {
			return this.maxBudgetRemaining < 0;
		},
		isButtonDisabled() {
			return this.noBudgetLeft || !this.areAllBudgetsSet();
		}
	},
	watch: {
		savingsSubcategories(value) {
			this.setSavingsSubcategories(value);
		},
		debtsSubcategories(value) {
			this.setDebtsSubcategories(value);
		},
		maxSavingsRemaining(savings) {
			if (savings <= 0) {
				this.savingsSubcategoriesMax = this.savings.subcategories;
			} else {
				this.savingsSubcategoriesMax = this.savings.subcategories;
			}
		}
	},
	created() {
		if (this.categoryGroups) {
			this.setCategoryTabs();
		}
	},
	mounted() {
		if (this.savingsSubcategories) {
			this.setSavingsSubcategories(this.savingsSubcategories);
		}

		if (!this.$store.getters.initialDebts) {
			this.$store.dispatch('getInitialDebt').then(() => {
				if (this.debtsSubcategories) {
					this.setDebtsSubcategories(this.debtsSubcategories);
				}
			});
		}

		this.useValidations = this.userData.studentPreviousLifeStageId !== null;

		this.$refs.button.$el.focus();
	},
	methods: {
		setSavingsSubcategories(subcategories) {
			this.savings.subcategories = subcategories;
			this.savings.amount = this.getTotalSavingsAmount(true);
		},
		setDebtsSubcategories(subcategories) {
			this.debts.subcategories = subcategories;
			this.debts.amount = this.getTotalDebtsAmount(true);
		},
		toggleTooltip() {
			this.showTooltip = !this.showTooltip;
			this.showTooltipDesc = false;
			if (this.showTooltip) {
				this.$announcer.polite(this.$t('savingsTooltip'));
			} else {
				this.$announcer.polite('');
			}
		},
		savingsGoalTooltip(index) {
			this.showTooltip = false;
			this.selectedSubcategoryIndex =
				this.selectedSubcategoryIndex === index ? -1 : index;

			if (this.selectedSubcategoryIndex !== -1) {
				this.showTooltipDesc = true;
			}
		},
		areAllBudgetsSet() {
			let subcategory = null;

			for (let i = 0, len = this.savings.subcategories.length; i < len; i++) {
				subcategory = this.savings.subcategories[i];
				if (subcategory.amount <= 0) {
					return false;
				}
				if (
					subcategory.hasOwnProperty('totalAmount') &&
					subcategory.totalAmount <= 0
				) {
					return false;
				}
			}

			for (let i = 0, len = this.debts.subcategories.length; i < len; i++) {
				subcategory = this.debts.subcategories[i];
				if (subcategory.budget <= 0) {
					return false;
				}
				if (
					subcategory.hasOwnProperty('totalAmount') &&
					subcategory.totalAmount <= 0
				) {
					return false;
				}
			}

			for (let i = 0, len = this.categoryTabs.length; i < len; i++) {
				for (
					let j = 0, subLen = this.categoryTabs[i].subcategories.length;
					j < subLen;
					j++
				) {
					subcategory = this.categoryTabs[i].subcategories[j];
					if (subcategory.budget == 0) {
						return false;
					}
				}
			}

			return true;
		},
		calculateCategoryBudget(items) {
			return items.reduce((sum, item) => {
				if (item.budget >= 0) {
					return sum + parseInt(item.budget);
				}
			}, 0);
		},
		onSavingSliderChanged(subcategory) {
			this.savings.amount = this.getTotalSavingsAmount(true);

			this.announceBudgetChange(
				subcategory.name,
				subcategory.amount,
				subcategory.minimum,
				subcategory.maximum
			);
		},
		onDebtSliderChanged(subcategory) {
			const sumOfBudget = this.debts.subcategories.reduce(
				(sum, subcategory) => sum + subcategory.budget,
				0
			);
			this.debts.amount = sumOfBudget;

			this.announceBudgetChange(
				subcategory.name,
				subcategory.budget,
				subcategory.minimum,
				subcategory.maximum
			);
		},
		onExpenseSliderChanged(category, subcategory) {
			category.budget = category.subcategories.reduce((sum, item) => {
				if (item.budget >= 0) {
					return sum + parseInt(item.budget);
				}
			}, 0);

			this.announceBudgetChange(
				subcategory.name,
				subcategory.budget,
				subcategory.minimum,
				subcategory.maximum
			);
		},
		async announceBudgetChange(name, budget, minimum, maximum) {
			this.$announcer.polite(
				`${this.$t('budgetSet', {
					name: name,
					amount: budget
				})} ${this.getWarningMessage(budget, minimum, maximum)}`
			);

			await this.announceDelay(200);
			this.announceBudgetLeft();
		},
		getWarningMessage(budget, minimum, maximum) {
			if (minimum && budget < minimum) {
				return this.$t('budgetMinWarning');
			} else if (maximum && budget > maximum) {
				return this.$t('budgetMaxWarning');
			}
			return '';
		},
		announceBudgetLeft() {
			if (this.maxBudgetRemaining >= 0) {
				this.$announcer.polite(
					this.$t('budgetLeftToSpent', {
						amount: this.maxBudgetRemaining
					})
				);
			} else {
				this.announceError(this.$t('budgetUnsufficient'));
			}
		},
		announceTotalSavings(subcategory) {
			this.$announcer.polite(
				this.$t('totalSavingsAmountSet', {
					name: subcategory.name,
					amount: subcategory.totalAmount
				})
			);
		},
		toggleSliders(container, category) {
			category.expanded = !category.expanded;
		},
		cancel() {
			this.$emit('closeModal');
		},
		async checkAndSaveBudget() {
			if (this.areAllBudgetsSet() && !this.noBudgetLeft) {
				this.requestSent = true;
				this.$announcer.polite(this.$t('loading'));
				const goalsIds = this.savings.subcategories.map(i => i.studentGoalId);

				this.budgetCheckData = await StudentsService.checkBudgetForStudent(
					this.userData.studentId,
					this.lifeStageData.lifeStage_ID,
					goalsIds
				);

				this.saveBudget();
			}
		},
		async saveBudget() {
			if (this.areAllBudgetsSet() && !this.noBudgetLeft) {
				// this.requestSent = true;
				// this.$announcer.polite(this.$t('loading'));
				const expenseCategoryBudgets = [];

				const alreadyCompletedCategories = {};

				// remap categories as object with categoryId as key and budget as value
				this.budgetCheckData.categoriesBudgets.forEach(item => {
					alreadyCompletedCategories[item.expenseCategory_Id] = item.id;
				});

				this.categoryTabs.forEach(category => {
					category.subcategories.forEach(subcategory => {
						expenseCategoryBudgets.push({
							budget: subcategory.budget,
							expenseCategory_Id: subcategory.id,
							studentId: this.userData.studentId,
							id:
								subcategory.budgetID ||
								alreadyCompletedCategories[subcategory.id],
							lifeStage_ID: this.lifeStageData.lifeStage_ID
						});
					});
				});

				const response = await this.$apollo.mutate({
					mutation: gql`
						mutation(
							$expenseCategoryBudgets: [StudentExpenseCategoryBudgetInputObjectGraphType]!
						) {
							addOrUpdateIgnoreNullValuesManyStudentExpenseCategoryBudget(
								list: $expenseCategoryBudgets
							) {
								id
							}
						}
					`,
					variables: {
						expenseCategoryBudgets: expenseCategoryBudgets
					}
				});
				if (response.data) {
					this.saveSavings();
				} else {
					this.requestSent = false;
				}
			}
		},
		async updateStudentBudgetPopup() {
			this.requestSent = false;
			this.$emit('confirmModal');
		},
		async saveSavings() {
			if (this.areAllBudgetsSet()) {
				const savingsBudgets = [...this.savings.subcategories];

				savingsBudgets.forEach(item => {
					delete item.name;
					delete item.accountBalance;
					delete item.minimum;
					delete item.maximum;
					delete item.description;
				});

				const response = await this.$apollo.mutate({
					mutation: gql`
						mutation(
							$savingsBudgets: [StudentGoalSavingInputObjectGraphType]!
						) {
							addOrUpdateIgnoreNullValuesManyStudentGoalSaving(
								list: $savingsBudgets
							) {
								id
							}
						}
					`,
					variables: {
						savingsBudgets: savingsBudgets
					}
				});
				if (response.data) {
					this.saveDebtsBudget();
				}
			}
		},
		async saveDebtsBudget() {
			if (this.areAllBudgetsSet()) {
				const debtsBudgets = [...this.debts.subcategories];

				let lifeStage_ID;
				let studentId;
				let debtType;

				const alreadyCreatedDebts = {};

				this.budgetCheckData.debts.forEach(item => {
					alreadyCreatedDebts[item.debtType] = item;
				});

				debtsBudgets.forEach((item, index, array) => {
					const { budget } = item;

					lifeStage_ID = this.lifeStageData.lifeStage_ID;

					studentId = this.userData.studentId;
					if (item.name === debtNames.CREDIT_CARD) {
						debtType = debtTypes.CREDIT_CARD;
					} else if (item.name === debtNames.EDUCATION) {
						debtType = debtTypes.EDUCATION;
					}

					const debtToAdd = {
						budget,
						lifeStage_ID,
						studentId,
						debtType
					};

					const completedDebt = alreadyCreatedDebts[debtType];
					// if Item has already an Id - use it
					if (item.id) {
						debtToAdd.id = item.id;
					} else if (completedDebt && completedDebt.id) {
						// Otherwise check if the data is already presented in the BE
						// If it is - set that id which will  be resolved as Update
						debtToAdd.id = completedDebt.id;
					}

					array[index] = debtToAdd;
				});

				const response = await this.$apollo.mutate({
					mutation: gql`
						mutation($debtsBudgets: [StudentDebtBudgetInputObjectGraphType]!) {
							addOrUpdateIgnoreNullValuesManyStudentDebtBudget(
								list: $debtsBudgets
							) {
								id
							}
						}
					`,
					variables: {
						debtsBudgets: debtsBudgets
					}
				});

				if (response.data) {
					this.updateStudentBudgetPopup();
				}
			}
		},
		hasValidOptions(category) {
			// always true if we do not want to use validations
			if (!this.useValidations) {
				return true;
			}

			let key = 'budget';
			let useTotalAmount = false;
			// savings uses amount instead of budget
			if (category.name.toLowerCase() === this.$t('savings').toLowerCase()) {
				key = 'amount';
				useTotalAmount = true;
			}

			const t = category.subcategories.every(subcategory => {
				if (useTotalAmount) {
					return subcategory.totalAmount !== 0 && subcategory[key] !== 0;
				}
				return subcategory[key] !== 0;
			});

			return t;
		},
		validationClasses(value) {
			if (!this.useValidations) {
				return '';
			}
			value = value || 0;
			return value === 0 ? 'input--danger' : '';
		},
		getMaxSliderValue() {
			return this.netMonthlyIncome;
			// TODO: Use this after component is optimized
			// because this causes performance issues (visual lagging);

			// Add the value of the current category
			// since it is subtracted already in "maxBudgetRemaining"
			// If the first category is set to max (NMI), this will result
			// in MAX = 0 for each other category

			// return this.maxBudgetRemaining + value;
		}
	}
};
</script>
<style lang="scss" scoped>
.budget-creator-wrapper {
	width: 100%;
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	overflow: hidden;

	.text {
		&--small {
			font-size: 12px;
			vertical-align: middle;
			line-height: 24px;
		}

		&--danger {
			color: $font-red-alert;
		}
	}

	.input--danger {
		outline-color: $font-red-alert;
		outline-style: auto;
		outline-width: initial;
	}

	.budget-counter {
		margin-top: 8px;
		margin-bottom: 24px;
		color: $font-header;

		&.error {
			color: $font-red-alert;
		}
	}

	.scrollbar {
		flex-grow: 1;
	}

	.budget-creator {
		padding: 0 32px 24px 32px;
		max-height: 50vh;
		overflow-y: auto;

		@media screen and (min-width: 1280px) and (max-width: 1440px) {
			max-height: 30vh;
		}

		@media screen and (min-width: 1440px) {
			max-height: 45vh;
		}

		.category-cnt {
			width: 100%;
			background-color: $page-bgcolor;
			border-radius: 8px;
			margin-bottom: 8px;

			&.savings {
				.sliders-cnt {
					.slider-wrapper {
						&:not(:last-child) {
							border-bottom: 1px solid $container-border;
							padding-bottom: 24px;
							margin-top: 24px;
						}
					}
				}
			}

			&.expanded {
				.category-header {
					border-bottom: 1px solid $container-border;
					button {
						height: 24px;
						width: 24px;
					}
					svg {
						transform: rotate(270deg);
						transition: transform 0.5s linear;
					}
				}
			}

			&:hover {
				cursor: pointer;

				.category-header {
					button {
						height: 24px;
						width: 24px;
					}
					svg {
						fill: $hover-state;
					}
				}
			}

			.category-header {
				display: flex;
				align-items: center;
				padding: 24px 16px;
				width: 100%;
				background-color: transparent;
				border: 0;

				.heading-3 {
					flex-grow: 1;
					text-align: left;
					margin: 0;
				}

				.heading-4 {
					margin: 0 16px;
				}
				button {
					height: 24px;
					width: 24px;
				}

				svg {
					transform: rotate(90deg);
					transition: transform 0.5s linear;
					width: 6px;
					height: 10px;
					fill: $primary-state;
				}
			}

			.sliders-cnt {
				margin: 0 32px 32px 32px;
				padding-bottom: 16px;
				position: relative;

				.legend-wrapper {
					margin-top: 16px;
				}

				.slider-wrapper {
					position: relative;
					padding-bottom: 16px;
					&:first-child {
						margin-top: 24px;
					}
					&:not(:first-child) {
						margin-top: 16px;
					}
					.slider-info {
						text-align: left;
						&-name {
							display: flex;
							gap: 5px;
						}

						.saving-amount-wrapper {
							width: 100%;
						}

						.body-text-2 {
							display: inline-block;
						}

						.tooltip-wrapper {
							position: relative;
							display: inline-block;

							.tooltip-button {
								background-color: transparent;
								border: 0;
								padding: 0;
								margin-left: 4px;

								::v-deep path {
									fill: $info-icon-color;
								}
							}

							.tooltip-msg {
								transform: translate(36px, -50%);
								top: 14px;

								@media (min-width: $breakpoint-mid) {
									width: 320px;
									font-size: 24px;
								}
							}

							svg {
								height: 24px;
								width: 24px;
								&:hover {
									cursor: pointer;
								}
							}
						}
					}

					.msg {
						width: 100%;
						position: absolute;
						bottom: 10px;
						text-align: right;

						&.min-msg {
							color: $budget-min-color;
						}

						&.max-msg {
							color: $budget-max-color;
						}
					}

					.slider-name {
						text-align: left;
						text-transform: capitalize;
						margin-bottom: 16px;
						display: block;
					}

					.body-text-2 {
						margin-bottom: 16px;
					}

					.budget-slider-wrapper {
						display: flex;

						.budget-slider {
							flex-grow: 1;
						}

						.budget-input {
							margin-left: 16px;
							margin-top: -8px;
							width: 80px;
						}
					}
				}
			}
		}
	}

	.slide-enter {
		transform: scaleY(0);
	}

	.slide-enter-active {
		transition: transform 0.5s ease;
		transform-origin: top;
	}

	.slide-leave-to {
		transform: scaleY(0);
	}

	.slide-leave-active {
		transition: transform 0.3s ease;
		transform-origin: top;
	}
}
</style>
